import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Hand, Truck, Star, ChevronDown } from 'lucide-react';

// Cinematic background frames (swap with real <video src=""/> when video assets are ready)
const HERO_FRAMES = [
  'https://customer-assets-agu9un31.emergentagent.net/job_cd7911c0-2a95-487c-9654-01df6f4c012d/artifacts/snkyfoay_IMG_1417.webp',
  'https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/j2uwmz8f_68BB0444-5D6B-4E65-94A6-BFF19BB84356.JPG',
  'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/i48bb4l4_IMG_6083.jpeg',
  'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/ppe6z1v6_IMG_6036.jpeg',
  'https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/pk46nxgj_IMG_5015.jpeg',
  'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/205ctg8c_IMG_5959.jpeg',
  'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/yebg53s9_IMG_6027%202.jpeg',
  'https://customer-assets.emergentagent.com/job_d42f1bf2-eb5e-4681-9fcb-1be42bf600c3/artifacts/o3mi6vr4_IMG_6014.jpeg',
  'https://customer-assets.emergentagent.com/job_c73da57c-4473-4a8c-88b1-60cebc2d9ba3/artifacts/0vk7eqoe_IMG_6316.jpeg',
];

const FRAME_DURATION = 5500; // ms per frame
const FADE_DURATION = 2.6;   // seconds — cross-fade between frames

const scrollToNextSection = () => {
  const hero = document.querySelector('[data-testid="hero-section"]');
  const next = hero?.nextElementSibling;
  if (next) {
    next.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  }
};

const trustItems = [
  { icon: Star, label: 'Handmade in Canada' },
  { icon: Hand, label: 'Fully Customizable' },
  { icon: Truck, label: 'Shipping Available' },
  { icon: Sparkles, label: 'One-of-a-kind' },
];

export const HeroSection = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // Preload all frames for buttery-smooth fades. Empty deps are intentional
    // — HERO_FRAMES is a module constant and setIdx uses a functional update,
    // so no closure over stale values exists. This runs exactly once on mount.
    HERO_FRAMES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    const id = setInterval(() => setIdx((p) => (p + 1) % HERO_FRAMES.length), FRAME_DURATION);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      className="relative h-screen w-full overflow-hidden flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* ─── Cinematic background montage (Ken Burns) ─── */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="sync">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1.18 }}
            exit={{ opacity: 0, scale: 1.22 }}
            transition={{
              opacity: { duration: FADE_DURATION, ease: [0.42, 0, 0.58, 1] },
              scale: { duration: 8, ease: 'linear' },
            }}
            className="absolute inset-0"
          >
            <img
              src={HERO_FRAMES[idx]}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
              draggable="false"
            />
          </motion.div>
        </AnimatePresence>

        {/* Ocean-inspired tint */}
        <div
          className="absolute inset-0 mix-blend-multiply"
          style={{
            background:
              'linear-gradient(135deg, rgba(8,28,40,0.55) 0%, rgba(12,46,58,0.45) 40%, rgba(0,0,0,0.65) 100%)',
          }}
        />
        {/* Bottom legibility wash */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/80" />
        {/* Soft film grain */}
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27160%27 height=%27160%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.6%27/></svg>")',
          }}
        />
        {/* Subtle metallic gold shimmer */}
        <motion.div
          aria-hidden
          animate={{ x: ['-30%', '130%'] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-1/4 left-0 w-1/3 h-[150%] pointer-events-none opacity-[0.08]"
          style={{
            background:
              'linear-gradient(115deg, transparent 0%, rgba(212,175,90,0.0) 35%, rgba(212,175,90,0.55) 50%, rgba(212,175,90,0.0) 65%, transparent 100%)',
            filter: 'blur(8px)',
          }}
        />
      </div>

      {/* ─── Center content ─── */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 text-center">
        <div className="max-w-4xl mx-auto flex flex-col items-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            className="text-[11px] sm:text-xs uppercase tracking-[0.5em] mb-6"
            style={{ color: '#D4AF5A' }}
            data-testid="hero-eyebrow"
          >
            Navs Art Studio · Vancouver
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25, ease: 'easeOut' }}
            className="text-white text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.05] mb-6 tracking-[-0.01em]"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
            data-testid="hero-title"
          >
            Art that tells <em className="italic" style={{ color: '#E8D9B5' }}>your story</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45, ease: 'easeOut' }}
            className="text-white/85 text-base sm:text-lg max-w-2xl leading-relaxed mb-10"
            data-testid="hero-subtitle"
          >
            From ocean-inspired creations to personalized gifts and custom designs — every piece is
            handcrafted in Vancouver and made uniquely for you.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: 'easeOut' }}
            className="flex flex-col sm:flex-row items-center gap-4 mb-12"
          >
            <Link to="/contact" data-testid="hero-cta-custom">
              <button
                className="group relative overflow-hidden rounded-full px-8 sm:px-10 py-4 sm:py-[18px] text-sm tracking-[0.18em] uppercase font-medium text-[#0B1F26] transition-all duration-500 hover:shadow-[0_10px_40px_rgba(212,175,90,0.45)] hover:-translate-y-0.5"
                style={{
                  background:
                    'linear-gradient(135deg, #F1E0B0 0%, #D4AF5A 55%, #B8923E 100%)',
                }}
              >
                <span className="relative z-10 flex items-center gap-3">
                  Create Your Custom Piece
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      'linear-gradient(135deg, #E8D9B5 0%, #C9A256 50%, #A37B2F 100%)',
                  }}
                />
              </button>
            </Link>

            <Link to="/featured" data-testid="hero-cta-featured">
              <button className="group rounded-full px-8 sm:px-10 py-4 sm:py-[18px] text-sm tracking-[0.18em] uppercase font-medium text-white border border-white/40 backdrop-blur-md bg-white/5 hover:bg-white/15 hover:border-white/80 transition-all duration-500 hover:-translate-y-0.5">
                <span className="flex items-center gap-3">
                  Explore Collection
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </Link>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: 'easeOut' }}
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-10"
            data-testid="hero-trust-row"
          >
            {trustItems.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className="flex items-center gap-2 text-white/75 text-[11px] sm:text-xs uppercase tracking-[0.18em]"
                data-testid={`hero-trust-${i}`}
              >
                <Icon size={14} strokeWidth={1.5} className="text-[#D4AF5A]" />
                <span>{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Frame indicators */}
      <div className="absolute bottom-24 sm:bottom-28 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {HERO_FRAMES.map((src, i) => (
          <span
            key={src}
            className="block h-[2px] transition-all duration-500"
            style={{
              width: i === idx ? 28 : 12,
              background: i === idx ? 'rgba(212,175,90,0.9)' : 'rgba(255,255,255,0.35)',
            }}
          />
        ))}
      </div>

      {/* ─── Auto-scroll cue ─── */}
      <motion.button
        type="button"
        onClick={scrollToNextSection}
        aria-label="Scroll to explore"
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.2, ease: 'easeOut' }}
        className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 z-10 group flex flex-col items-center gap-2 cursor-pointer focus:outline-none"
      >
        <span
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.42em] text-white/70 group-hover:text-white transition-colors duration-300"
          style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.42em' }}
        >
          Scroll
        </span>
        <motion.span
          aria-hidden
          animate={{ y: [0, 8, 0], opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex items-center justify-center w-9 h-9 rounded-full border border-white/30 backdrop-blur-md bg-white/5 group-hover:border-[#D4AF5A]/80 group-hover:bg-[#D4AF5A]/10 transition-all duration-300"
        >
          <ChevronDown size={16} strokeWidth={1.5} className="text-white/90 group-hover:text-[#D4AF5A] transition-colors" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default HeroSection;
